import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/privacy.scss";

const privacySections = [
  ["information", "Information we collect"],
  ["use", "How we use information"],
  ["cookies", "Cookies and analytics"],
  ["sharing", "When information is shared"],
  ["retention", "Retention and security"],
  ["rights", "Your choices and rights"],
  ["transfers", "International transfers"],
  ["updates", "Updates and contact"],
];

function Privacy() {
  const { brandName, contact } = siteConfig;

  return (
    <main className="policy-page privacy" aria-labelledby="privacy-title">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Legal / Privacy</p>
        <h1 id="privacy-title">Privacy policy</h1>
        <div className="policy-page__summary">
          <p>
            This policy explains how {brandName} collects, uses and protects
            personal information when you browse our store, contact us or place
            an order.
          </p>
          <time dateTime="2026-08-14">Last updated 14 August 2026</time>
        </div>
      </header>

      <div className="policy-page__layout">
        <nav className="policy-page__contents" aria-label="Privacy policy contents">
          <p>On this page</p>
          <ol>
            {privacySections.map(([id, label], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="policy-page__content">
          <section id="information">
            <h2>Information we collect</h2>
            <p>
              We collect information you choose to provide, such as your name,
              email address, telephone number, delivery and billing addresses,
              order details and messages sent to client care. Payment details
              are processed by our payment providers and are not stored in full
              by {brandName}.
            </p>
            <p>
              When you use the site, technical information may be collected
              automatically, including device and browser type, IP address,
              approximate location, pages viewed, referring pages and the time
              of your visit.
            </p>
          </section>

          <section id="use">
            <h2>How we use information</h2>
            <p>We use personal information when necessary to:</p>
            <ul>
              <li>process, deliver and support your orders;</li>
              <li>respond to questions, returns and product enquiries;</li>
              <li>operate, secure and improve the website;</li>
              <li>prevent fraud and comply with legal obligations; and</li>
              <li>
                send news or product updates when you have asked to receive
                them.
              </li>
            </ul>
            <p>
              Depending on the context, our legal basis is performance of a
              contract, a legal obligation, our legitimate interests or your
              consent. You may withdraw consent at any time.
            </p>
          </section>

          <section id="cookies">
            <h2>Cookies and analytics</h2>
            <p>
              Cookies and similar technologies can keep the cart working,
              remember preferences, understand site performance and help us
              provide relevant communications. Essential technologies are used
              where required for the store to function; optional technologies
              are used only where permitted.
            </p>
            <p>
              You can adjust browser settings to block or delete cookies.
              Disabling essential storage may prevent parts of the store from
              working correctly.
            </p>
          </section>

          <section id="sharing">
            <h2>When information is shared</h2>
            <p>
              We share only what is reasonably necessary with trusted providers
              that help us operate the store, including hosting, payment,
              delivery, analytics, communications and client-care services.
              They may use information only to provide their contracted service
              or as required by law.
            </p>
            <p>
              We may also disclose information to meet a legal requirement,
              protect customers and our rights, investigate misuse, or as part
              of a business reorganisation. We do not sell personal information.
            </p>
          </section>

          <section id="retention">
            <h2>Retention and security</h2>
            <p>
              Information is kept only for as long as needed for the purpose for
              which it was collected, including fulfilment, warranty, tax,
              accounting, dispute and fraud-prevention requirements. Retention
              periods vary according to the record and applicable law.
            </p>
            <p>
              We use proportionate administrative and technical safeguards.
              No online system is completely secure, so please avoid sending
              sensitive payment information through email or the contact form.
            </p>
          </section>

          <section id="rights">
            <h2>Your choices and rights</h2>
            <p>
              Subject to local law, you may request access to, correction of,
              deletion of or a copy of your personal information. You may also
              object to or restrict certain processing and withdraw consent.
            </p>
            <p>
              To protect your information, we may need to verify your identity.
              You may unsubscribe from marketing through the link in any
              message. You also have the right to contact your local data
              protection authority.
            </p>
          </section>

          <section id="transfers">
            <h2>International transfers</h2>
            <p>
              Some providers may process information outside your country. When
              required, we use recognised safeguards for these transfers, such
              as adequacy decisions or approved contractual clauses, together
              with appropriate security measures.
            </p>
          </section>

          <section id="updates">
            <h2>Updates and contact</h2>
            <p>
              We may update this policy as the store or applicable requirements
              change. The date above shows the latest revision. Material changes
              will be communicated where required.
            </p>
            <div className="policy-page__callout">
              <p>Questions or privacy requests</p>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
              <span>{contact.address}</span>
              <Link to="/contact">Open the contact form ↗</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
